import * as React from 'react';
import { createPortal } from 'react-dom';
import { HyperlinkFieldProps, useHyperlinkFieldController } from './hyperlink-field.controller';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { getHeadingAlignmentStyle, getHeadingPositionOffset } from '../field-heading.utils';
import { Flex } from '@common/components/Flex';
import { FieldContainer } from '../field-container';
import { HyperlinkEditor } from '@common/components/hyperlink/HyperlinkEditor';
import { UploadModal } from '@common/components/upload-modal/UploadModal';
import { useFieldContentStyle } from '../content-style.utils';
import { openDocument, downloadDocument } from '@common/actions/table';
import { isEmail } from '@common/utils/email';

import './hyperlink-field.styles.scss';
import { MessageBox } from '@common/components/MessageBox';
import { useForceDisabled } from '../../context';

export const HyperlinkField: React.FC<HyperlinkFieldProps> = (props) => {
  const { datasheetElement, context, globalId } = props;

  const {
    fieldCurrentValue,
    handleFieldValueChange,
    handleFieldValueSubmit,
    handleFieldValueBlur,
    handleUploadClick,
    showUploadModal,
    setShowUploadModal,
    handleFileUpload,
    headingPlacedHorizontally,
    headingReference,
    headingSize,
    headingPosition,
    messageProps,
    closeMessage,
    inputRef,
    handleTabNavigation,
    isEditable,
  } = useHyperlinkFieldController(props);

  const forceDisabled = useForceDisabled();
  const { formControlStyle } = useFieldContentStyle(datasheetElement.original);

  const fieldWidth = datasheetElement.original.width;
  const fieldHeight = datasheetElement.original.height;

  const headingAlignmentStyle = getHeadingAlignmentStyle({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    fieldWidth,
  });

  const headingPositionOffset = getHeadingPositionOffset({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    headingSize,
    fieldWidth,
    fieldHeight,
  });

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext}
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      absolutePosition={true}
      alignmentStyle={headingAlignmentStyle}
      positionOffset={headingPositionOffset}
    />
  );

  // ----- Hyperlink behaviour (mirrors spreadsheet cell.tsx) -----
  const [showLinkPopup, setShowLinkPopup] = React.useState(false);
  const [popupPos, setPopupPos] = React.useState({ x: 0, y: 0 });
  const [isEditing, setIsEditing] = React.useState(false);
  const closePopupTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const isEmailAddress = React.useMemo(() => {
    if (!fieldCurrentValue) return false;
    return isEmail(fieldCurrentValue);
  }, [fieldCurrentValue]);

  const isWebLink = React.useMemo(() => {
    if (!fieldCurrentValue || typeof fieldCurrentValue !== 'string') return false;
    const lowerValue = fieldCurrentValue.toLowerCase();
    return lowerValue.startsWith('https://') || lowerValue.startsWith('http://') || lowerValue.startsWith('www');
  }, [fieldCurrentValue]);

  const isPlainTextDocument = React.useMemo(() => {
    // Only show Open/Download for hyperlink fields that are neither email nor web link
    return !!fieldCurrentValue && !isEmailAddress && !isWebLink;
  }, [fieldCurrentValue, isEmailAddress, isWebLink]);

  const handleLinkMouseEnter = React.useCallback((e: React.MouseEvent) => {
    if (!fieldCurrentValue) return;
    // Only prevent popup when actively editing (typing in input)
    if (isEditing && isEditable) return;
    // Clear any pending close timeout
    if (closePopupTimeoutRef.current) {
      clearTimeout(closePopupTimeoutRef.current);
      closePopupTimeoutRef.current = null;
    }
    // Position popup near mouse cursor
    setPopupPos({
      x: e.clientX,
      y: e.clientY + 8,
    });
    setShowLinkPopup(true);
  }, [fieldCurrentValue, isEditing, isEditable]);

  const handleLinkMouseLeave = React.useCallback(() => {
    // Don't close immediately - wait a bit to see if mouse enters popup
    closePopupTimeoutRef.current = setTimeout(() => {
      setShowLinkPopup(false);
      closePopupTimeoutRef.current = null;
    }, 150); // 150ms delay to allow mouse to move to popup
  }, []);

  const handlePopupMouseEnter = React.useCallback(() => {
    // Cancel any pending close when mouse enters popup
    if (closePopupTimeoutRef.current) {
      clearTimeout(closePopupTimeoutRef.current);
      closePopupTimeoutRef.current = null;
    }
    setShowLinkPopup(true);
  }, []);

  const handlePopupMouseLeave = React.useCallback(() => {
    setShowLinkPopup(false);
  }, []);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (closePopupTimeoutRef.current) {
        clearTimeout(closePopupTimeoutRef.current);
      }
    };
  }, []);

  // Reset editing state when field value changes (e.g., after upload)
  React.useEffect(() => {
    if (fieldCurrentValue) {
      setIsEditing(false);
    }
  }, [fieldCurrentValue]);

  const handleOpenClick = React.useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!fieldCurrentValue || !globalId || !context) {
        return;
      }

      const field: any = datasheetElement.original.field;
      if (!field?.fieldid) return;

      const fieldId = field.fieldid;
      const tableId = field.table ? field.fieldid : 0;
      const rrn = field.table ? 1 : -1;

      try {
        await openDocument(
          String(fieldCurrentValue),
          fieldId,
          context,
          globalId,
          tableId,
          rrn
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to open document:', error);
      }
    },
    [fieldCurrentValue, globalId, context, datasheetElement]
  );

  const handleDownloadClick = React.useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!fieldCurrentValue || !globalId || !context) {
        return;
      }

      const field: any = datasheetElement.original.field;
      if (!field?.fieldid) return;

      const fieldId = field.fieldid;
      const tableId = field.table ? field.fieldid : 0;
      const rrn = field.table ? 1 : -1;

      try {
        await downloadDocument(
          String(fieldCurrentValue),
          fieldId,
          context,
          globalId,
          tableId,
          rrn
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to download document:', error);
      }
    },
    [fieldCurrentValue, globalId, context, datasheetElement]
  );

  const fieldIsEmpty = (() => { const v = (datasheetElement.original as any).field?.name ?? (datasheetElement.original as any).field?.value; return v == null || String(v).trim() === ''; })();

  const getLinkText = React.useMemo(() => {
    if (!fieldCurrentValue) return '';

    if (isEmailAddress) {
      return fieldCurrentValue;
    }
    if (isWebLink) {
      return `Browse to ${fieldCurrentValue}`;
    }
    return fieldCurrentValue;
  }, [fieldCurrentValue, isEmailAddress, isWebLink]);

  const getLinkHref = React.useMemo(() => {
    if (!fieldCurrentValue) return '#';

    if (isEmailAddress) {
      return `mailto:${fieldCurrentValue}`;
    }

    if (isWebLink) {
      let url = fieldCurrentValue;
      if (url.toLowerCase().startsWith('www')) {
        url = `http://${url}`;
      }
      return url;
    }

    return '#';
  }, [fieldCurrentValue, isEmailAddress, isWebLink]);

  const handleOpenEmail = React.useCallback((mailtoUrl: string) => {
    // Store original beforeunload handler
    const originalBeforeUnload = window.onbeforeunload;
    let mailtoClickInProgress = true;
    const startTime = Date.now();
    const PROTECTION_DURATION = 5000; // 5 seconds

    // Override beforeunload temporarily to prevent logout (mirrors spreadsheet behaviour)
    window.onbeforeunload = function (e: BeforeUnloadEvent) {
      if (mailtoClickInProgress && Date.now() - startTime < PROTECTION_DURATION) {
        return undefined;
      }
      if (originalBeforeUnload) {
        return originalBeforeUnload.call(window, e);
      }
      return undefined;
    };

    window.open(mailtoUrl, '_blank');
    setShowLinkPopup(false);

    setTimeout(() => {
      mailtoClickInProgress = false;
      window.onbeforeunload = originalBeforeUnload;
    }, PROTECTION_DURATION);
  }, []);

  const handleBrowseToClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isWebLink && getLinkHref !== '#') {
        window.open(getLinkHref, '_blank', 'noopener,noreferrer');
        setShowLinkPopup(false);
      }
    },
    [isWebLink, getLinkHref]
  );

  const handleEmailClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isEmailAddress && getLinkHref !== '#') {
        handleOpenEmail(getLinkHref);
      }
    },
    [isEmailAddress, getLinkHref, handleOpenEmail]
  );

  return (
    <>
          <MessageBox hideMessageBox={closeMessage} {...messageProps} />

      <DatasheetItem
        style={{
          top: datasheetElement.coordinates.y,
          left: datasheetElement.coordinates.x,
        }}
      >
        {labelComponent}
        <Flex alignItems="center" style={{ position: 'relative' }}>
          <FieldContainer
            fieldId={datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id}
            showBorder={datasheetElement.original.showborder}
            width={fieldWidth}
            height={fieldHeight}
            className={!isEditable ? 'DatasheetsResultHyperlinkField__field-container--disabled' : ''}
            required={datasheetElement.original.required}
            isEmpty={fieldIsEmpty}
          >
            <HyperlinkEditor
              inputRef={inputRef}
              value={forceDisabled ? '' : fieldCurrentValue}
              onChange={(value: string) => {
                if (isEditable) {
                  handleFieldValueChange(value);
                  setIsEditing(true);
                  setShowLinkPopup(false);
                }
              }}
              onUpload={isEditable ? handleUploadClick : () => {}}
              onMouseLeave={handleLinkMouseLeave}
              onMouseEnter={handleLinkMouseEnter}
              onClick={() => {
                if (isEditable) {
                  setIsEditing(true);
                  setShowLinkPopup(false);
                }
              }}
              onSubmit={isEditable ? handleFieldValueSubmit : () => {}}
              onBlur={isEditable ? handleFieldValueBlur : undefined}
              onKeyDown={handleTabNavigation}
              disabled={!isEditable}
              className="DatasheetsResultHyperlinkField__editor"
              style={formControlStyle}
            />
          </FieldContainer>
        </Flex>
      </DatasheetItem>
      <UploadModal
        isOpen={showUploadModal && isEditable}
        onClose={() => {
          setShowUploadModal(false);
          setIsEditing(false); 
        }}
        onUpload={isEditable ? async (file: File) => {
          await handleFileUpload(file);
          setIsEditing(false);
        } : () => {}}
        title="Upload Document"
      />

      {/* Hover popup for all hyperlink types */}
      {showLinkPopup &&
        Boolean(fieldCurrentValue) &&
        createPortal(
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup DatasheetsResultHyperlinkField__link-popup"
            style={{
              left: popupPos.x,
              top: popupPos.y,
            }}
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
          >
            <div>
              {isPlainTextDocument ? (
                <>
                  <div
                    onClick={handleOpenClick}
                    className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__anchor"
                  >
                    Open
                  </div>
                  <div
                    onClick={handleDownloadClick}
                    className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__anchor"
                  >
                    Download
                  </div>
                </>
              ) : isEmailAddress ? (
                <div
                  onClick={handleEmailClick}
                  className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__anchor"
                >
                  {getLinkText}
                </div>
              ) : isWebLink ? (
                <div
                  onClick={handleBrowseToClick}
                  className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__anchor"
                >
                  {getLinkText}
                </div>
              ) : null}
            </div>
            <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__arrow" />
          </div>,
          document.body
        )}
    </>
  );
};
